import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiSearch, FiZap } from "react-icons/fi";
import { getSession } from "../utils/session";
import ChatConversacion from "./ChatConversacion";
import Avatar from "./Avatar";
import { API_URL } from "../services/api";

// ponytail: sondeo cada 5 s; pasar a Socket.IO (ya está en el backend) si hace falta inmediatez
const SONDEO_MS = 5000;

const nombre = (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email;

// "hace 5 min", "ayer", "12 oct": en una bandeja de entrada la hora exacta no aporta
function cuando(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const min = Math.round((Date.now() - d) / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `${min} min`;
  if (min < 60 * 24) return `${Math.round(min / 60)} h`;
  const ayer = min < 60 * 48;
  if (ayer) return "ayer";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

const resumen = (m) => {
  if (!m) return "Sin mensajes todavía";
  if (m.text) return m.text;
  if (m.imagenTipo) return "📷 Foto";
  if (m.videoTipo) return "🎥 Vídeo";
  return "";
};

export default function AdminChatPanel() {
  const [convs, setConvs] = useState(null);
  const [activeUid, setActiveUid] = useState(null);
  const [error, setError] = useState("");
  const [busca, setBusca] = useState("");

  useEffect(() => {
    let vivo = true;
    async function fetchConvs() {
      try {
        const { token } = await getSession();
        const res = await axios.get(`${API_URL}/chat/admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (vivo) {
          setConvs(res.data);
          setError("");
        }
      } catch (err) {
        if (vivo) setError("Error al cargar conversaciones.");
      }
    }
    fetchConvs();
    const id = setInterval(fetchConvs, SONDEO_MS);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, []);

  const current = convs?.find((conv) => conv.user._id === activeUid);

  const cambiarActual = (fn) =>
    setConvs((convs) =>
      convs.map((conv) => (conv.user._id === activeUid ? { ...conv, messages: fn(conv.messages) } : conv))
    );

  /*
   * Orden de la bandeja: primero quien espera respuesta, y dentro de cada
   * grupo lo más reciente arriba. Los que nunca han escrito van al final,
   * pero siguen estando: hace falta poder escribirles primero.
   */
  const lista = useMemo(() => {
    if (!convs) return [];
    const texto = busca.trim().toLowerCase();
    return convs
      .filter((c) => !texto || nombre(c.user).toLowerCase().includes(texto) || (c.user.email || "").toLowerCase().includes(texto))
      .map((c) => {
        const ultimo = c.messages.at(-1);
        return {
          ...c,
          ultimo,
          espera: ultimo?.from === "user",
          // Mensajes suyos seguidos al final, sin responder: lo que lleva esperando
          sinLeer: (() => {
            let n = 0;
            for (let i = c.messages.length - 1; i >= 0; i -= 1) {
              if (c.messages[i].from !== "user") break;
              n += 1;
            }
            return n;
          })(),
        };
      })
      .sort((a, b) => {
        if (a.espera !== b.espera) return a.espera ? -1 : 1;
        return new Date(b.ultimo?.timestamp || 0) - new Date(a.ultimo?.timestamp || 0);
      });
  }, [convs, busca]);

  const esperando = lista.filter((c) => c.espera).length;

  return (
    <div className="admin-chat-container">
      <div className="admin-users-list">
        <div className="acl-top">
          <h4>
            Conversaciones
            {esperando > 0 && <span className="acl-badge">{esperando} esperan</span>}
          </h4>
          <label className="acl-busca">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar cliente"
              aria-label="Buscar cliente"
            />
          </label>
        </div>

        {convs?.length === 0 && <div className="app-estado">Aún no hay clientes.</div>}
        {convs && lista.length === 0 && busca && <div className="app-estado">Nadie con ese nombre.</div>}

        <ul>
          {lista.map((conv) => (
            <li
              key={conv.user._id}
              className={activeUid === conv.user._id ? "active" : ""}
              onClick={() => setActiveUid(conv.user._id)}
            >
              <Avatar
                userId={conv.user._id}
                nombre={conv.user.firstName}
                apellido={conv.user.lastName}
                tamano={40}
              />
              <div className="acl-datos">
                <div className="acl-fila">
                  <span className="acl-nombre">{nombre(conv.user)}</span>
                  <span className="acl-cuando">{cuando(conv.ultimo?.timestamp)}</span>
                </div>
                <div className="acl-fila">
                  <span className={conv.espera ? "acl-ultimo acl-ultimo-nuevo" : "acl-ultimo"}>
                    {conv.ultimo?.automatico && <FiZap aria-hidden="true" title="Mensaje automático" />}
                    {conv.ultimo?.from === "admin" && !conv.ultimo?.automatico && "Tú: "}
                    {resumen(conv.ultimo)}
                  </span>
                  {conv.sinLeer > 0 && <span className="acl-cuenta">{conv.sinLeer}</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="admin-chat-view">
        {error && <div className="app-estado app-estado-error">{error}</div>}
        {!convs ? (
          !error && <div className="app-estado">Cargando chats...</div>
        ) : current ? (
          <>
            <div className="acv-cabecera">
              <Avatar
                userId={current.user._id}
                nombre={current.user.firstName}
                apellido={current.user.lastName}
                tamano={38}
              />
              <div>
                <strong>{nombre(current.user)}</strong>
                <span>{current.user.email}</span>
              </div>
            </div>
            <ChatConversacion
              key={activeUid}
              messages={current.messages}
              mio="admin"
              otro={{
                id: current.user._id,
                nombre: current.user.firstName,
                apellido: current.user.lastName,
              }}
              enviarUrl={`${API_URL}/chat/admin`}
              extra={{ userId: activeUid }}
              placeholder={`Escribe a ${current.user.firstName || nombre(current.user)}...`}
              boton="Responder"
              onCambio={cambiarActual}
            >
              {current.messages.length === 0 && (
                <div className="app-estado">Sin mensajes todavía. Escríbele tú primero.</div>
              )}
            </ChatConversacion>
          </>
        ) : (
          <div className="app-estado">Elige una conversación de la lista.</div>
        )}
      </div>
    </div>
  );
}
